import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallbackPath?: string;
  fallbackLabel?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render-phase errors in child components and shows a recovery UI
 * instead of a blank page. Without this, any uncaught error in a route
 * component kills the entire React tree.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    console.error('[ErrorBoundary] caught render error', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleNavigate = () => {
    this.setState({ hasError: false, error: null });
    const path = this.props.fallbackPath || '/portal/teacher/storybooks';
    window.location.href = path;
  };

  render() {
    if (this.state.hasError) {
      const label = this.props.fallbackLabel || 'Back to Storybooks';
      return (
        <div className="mx-auto max-w-lg space-y-4 p-8">
          <h2 className="text-xl font-semibold text-cocoa">Something went wrong</h2>
          <p className="text-sm text-cocoa/70">
            This page ran into an error. You can try again or head back.
          </p>
          {this.state.error?.message && (
            <pre className="rounded-xl bg-rose-50 p-3 text-xs text-rose-700 overflow-auto">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              className="rounded-full border border-sand bg-white px-4 py-2 text-sm font-medium text-cocoa hover:bg-shell"
              onClick={this.handleReset}
            >
              Try again
            </button>
            <button
              type="button"
              className="rounded-full bg-brick px-4 py-2 text-sm font-medium text-white hover:bg-brick/90"
              onClick={this.handleNavigate}
            >
              {label}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
